/**
 * PlayerPhoto
 *
 * Renders a real player headshot when one resolves, initials otherwise.
 *
 * Source (wired 10 Oct 2026, probed live first per Hard Rule 3): ESPN's
 * public CDN, looked up by player NAME through useHeadshots -> POST
 * /api/players/headshots. The founder's call is to ship photos with a
 * "Photos: ESPN" credit (see <PhotoCredit /> in PlayerAvatar.jsx) and a kill
 * switch at both ends (VITE_PLAYER_HEADSHOTS / PLAYER_HEADSHOTS_ENABLED).
 *
 * Every caller already rendered this component in the right slot, so turning
 * photos on app-wide meant changing this file, not fourteen pages. While a
 * batch is loading — or when ESPN has no picture, or the image 404s — the
 * initials avatar shows, so rows never jump or go blank.
 *
 * Props:
 *   name       — Player full name (initials, alt text, and the lookup key)
 *   sport      — 'nba' | 'nfl' (default 'nba'); pass it so NFL pages resolve
 *   photoUrl   — Explicit headshot URL; skips the lookup when given
 *   size       — Width in px (height auto-scales at 190/260 ≈ 0.73 for a
 *                rectangular photo; `rounded="full"` renders a square circle)
 *   rounded    — 'none' | 'sm' | 'lg' | 'full'  (default 'lg')
 *   className  — Extra classes applied to the image
 *
 * (playerId is accepted but unused — the lookup is by name because the ids
 * JAI holds are platform ids, not ESPN's.)
 */

import { useMemo, useState } from 'react'
import { V2 } from './JaiV2'
import { JAI_FONTS } from './JaiBrand'
import useHeadshots from '../hooks/useHeadshots'

const RATIO = 190 / 260 // face card aspect ratio

/** "Nikola Jokic" -> "NJ", "Ja'Marr Chase" -> "JC", single-word names -> first 2 letters. */
function initialsFor(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

export default function PlayerPhoto({
  name = '',
  sport = 'nba',
  photoUrl = null,
  size = 80,
  rounded = 'lg',
  className = '',
}) {
  const names = useMemo(() => (photoUrl || !name ? [] : [name]), [photoUrl, name])
  const resolved = useHeadshots(sport, names)
  const url = photoUrl || resolved[name] || null

  // A 404 (rookie with no ESPN photo yet) swaps to initials; a new url for
  // the same slot gets a fresh chance to load.
  const [brokenUrl, setBrokenUrl] = useState(null)
  const broken = Boolean(url) && brokenUrl === url

  const radii = {
    none: '0',
    sm:   '6px',
    lg:   '12px',
    full: '50%',
  }
  const radius = radii[rounded] ?? '12px'

  if (!url || broken) {
    return (
      <span
        aria-hidden="true"
        title={name || undefined}
        className={className}
        style={{
          width: size,
          height: size,
          borderRadius: radius,
          background: V2.navyInk,
          color: 'rgba(255,255,255,0.85)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily: JAI_FONTS.mono,
          fontWeight: 700,
          fontSize: Math.max(10, Math.round(size * 0.36)),
          letterSpacing: '0.02em',
          flexShrink: 0,
        }}
      >
        {initialsFor(name)}
      </span>
    )
  }

  // A circle needs a square box or the face is squashed; rectangles keep the
  // face-card ratio the existing layouts were sized for.
  const width  = size
  const height = rounded === 'full' ? size : Math.round(size * RATIO)

  return (
    <img
      src={url}
      alt={name}
      className={className}
      loading="lazy"
      decoding="async"
      onError={() => setBrokenUrl(url)}
      style={{
        width,
        height,
        borderRadius: radius,
        objectFit: 'cover',
        // ESPN headshots are 200x145 with the face upper-middle; a slight
        // downward focal point keeps chins in frame when cropping to a circle.
        objectPosition: '50% 20%',
        background: V2.paper,
        flexShrink: 0,
      }}
    />
  )
}
